import { ErrorHandling } from "@sagecentral/core-ui";
import { AuthHelperUI } from "components/auth";
import Features from "data/features";
import { useFeatureEnabled } from "hooks/shared";
import React, { createContext, useContext, useEffect, useState } from "react";

type IFeatureGateContext = {
  setContent: (content: React.ReactNode) => void;
  setAlternateContent: (content: React.ReactNode) => void;
};

const FeatureGateContext = createContext<IFeatureGateContext>({
  setContent: () => {},
  setAlternateContent: () => {},
});

type FeatureGateChildProps = {
  children?: React.ReactNode;
};

type FeatureGateProps = {
  flag: Features;
  invert?: boolean;
  children?: React.ReactNode;
};

const FeatureGate = ({ flag, invert, children }: FeatureGateProps) => {
  const [enabled, loading] = useFeatureEnabled(flag);
  const [content, setContent] = useState<React.ReactNode | null>(null);
  const [altContent, setAltContent] = useState<React.ReactNode | null>(null);
  const [contentReady, setContentReady] = useState(false);
  const [altReady, setAltReady] = useState(false);

  const context: IFeatureGateContext = {
    setContent: (c) => {
      setContent(c);
      setContentReady(true);
    },
    setAlternateContent: (c) => {
      setAltContent(c);
      setAltReady(true);
    },
  };

  const isFlagDisabled = !enabled === !invert;

  return (
    <FeatureGateContext.Provider value={context}>
      <div style={{ display: "none" }}>{children}</div>

      {loading ? (
        <AuthHelperUI>Getting things ready...</AuthHelperUI>
      ) : isFlagDisabled ? (
        altReady ? (
          altContent ?? <ErrorHandling error={{ message: "Base Error", name: "Error", cause: 404 }} />
        ) : (
          <AuthHelperUI>Getting things ready...</AuthHelperUI>
        )
      ) : contentReady ? (
        content
      ) : (
        <AuthHelperUI>Getting things ready...</AuthHelperUI>
      )}
    </FeatureGateContext.Provider>
  );
};

const Content = ({ children }: FeatureGateChildProps) => {
  const context = useContext(FeatureGateContext);
  useEffect(() => {
    context.setContent(children);
  }, [children]);
  return null;
};

const AltContent = ({ children }: FeatureGateChildProps) => {
  const context = useContext(FeatureGateContext);
  useEffect(() => {
    context.setAlternateContent(children);
  }, [children]);
  return null;
};

FeatureGate.Content = Content;
FeatureGate.AltContent = AltContent;

export default FeatureGate;